import React, {useEffect, useState} from "react";
import styles, {C} from "../styles";
import {useConfirm, usePrompt} from "./FeedbackProvider";
import {
    apiAdminListIaQuizQuestions, apiAdminCreateIaQuizQuestion, apiAdminUpdateIaQuizQuestion,
    apiAdminActivateIaQuizQuestion, apiAdminSuspendIaQuizQuestion, apiAdminArchiveIaQuizQuestion,
    apiAdminFlagReviewIaQuizQuestion, apiAdminListIaQuizQuestionReviews,
    apiAdminGetIaQuizDashboard, apiAdminListIaQuizReports, apiAdminResolveIaQuizReport,
    apiAdminRunIaQuizReviewCycle, apiAdminEstimateIaQuizAiReview, apiAdminRunIaQuizAiReview,
} from "../api/adminIaQuiz";

const AXES = ["Culture IA", "Dialogue", "Fiabilité", "Agents", "Gouvernance", "Cybersécurité"];
const STATUSES = ["draft", "active", "review_required", "suspended", "archived"];
const EMPTY_FORM = {
    id: null, axis: AXES[0], level: 1,
    prompt: "", hint: "", optionA: "", optionB: "", optionC: "", optionD: "",
    correctOption: 0, feedback: "", sourceNotes: "",
};

export default function AdminIaQuizPanel() {
    const confirm = useConfirm();
    const prompt = usePrompt();
    const [questions, setQuestions] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [reports, setReports] = useState([]);
    const [filters, setFilters] = useState({axis: "", level: "", status: "", search: ""});
    const [form, setForm] = useState(EMPTY_FORM);
    const [message, setMessage] = useState("");
    const [estimate, setEstimate] = useState(null);
    const [aiRunResult, setAiRunResult] = useState(null);
    const [reviewCycleResult, setReviewCycleResult] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);
    const [history, setHistory] = useState([]);

    async function reload() {
        setQuestions(await apiAdminListIaQuizQuestions(filters));
        setDashboard(await apiAdminGetIaQuizDashboard());
        setReports(await apiAdminListIaQuizReports("open"));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [filters]);

    function updateForm(field, value) {
        setForm(prev => ({...prev, [field]: value}));
    }

    function startEdit(question) {
        setForm({
            id: question.id, axis: question.axis, level: question.level,
            prompt: question.prompt, hint: question.hint,
            optionA: question.option_a, optionB: question.option_b, optionC: question.option_c, optionD: question.option_d,
            correctOption: question.correct_option, feedback: question.feedback,
            sourceNotes: question.source_notes || "",
        });
    }

    function startDuplicate(question) {
        startEdit(question);
        setForm(prev => ({...prev, id: null}));
    }

    async function submitForm(e) {
        e.preventDefault();
        if (form.id) {
            await apiAdminUpdateIaQuizQuestion(form.id, form);
            setMessage("Question modifiée.");
        } else {
            await apiAdminCreateIaQuizQuestion(form);
            setMessage("Brouillon créé.");
        }
        setForm(EMPTY_FORM);
        reload();
    }

    async function transition(action, question) {
        const label = {activate: "activer", suspend: "suspendre", archive: "archiver"}[action];
        if (!(await confirm(`Confirmer : ${label} la question « ${question.prompt.slice(0, 60)} » ?`))) return;
        const reason = (await prompt("Motif (facultatif) :")) || "";
        const fn = {activate: apiAdminActivateIaQuizQuestion, suspend: apiAdminSuspendIaQuizQuestion, archive: apiAdminArchiveIaQuizQuestion}[action];
        await fn(question.id, reason);
        reload();
    }

    async function resolveReport(id) {
        await apiAdminResolveIaQuizReport(id);
        reload();
    }

    async function flagReview(question) {
        const reason = (await prompt("Motif du signalement pour revue :", {defaultValue: "Signalée par un participant"})) || "";
        await apiAdminFlagReviewIaQuizQuestion(question.id, reason);
        reload();
    }

    async function runReviewCycle() {
        setReviewCycleResult(await apiAdminRunIaQuizReviewCycle());
        reload();
    }

    async function toggleHistory(question) {
        if (historyFor === question.id) {
            setHistoryFor(null);
            return;
        }
        setHistory(await apiAdminListIaQuizQuestionReviews(question.id));
        setHistoryFor(question.id);
    }

    async function handleEstimate() {
        const {ok, data} = await apiAdminEstimateIaQuizAiReview();
        if (!ok) {
            setEstimate(null);
            setMessage(data.error || "Erreur lors de l'estimation du coût.");
            return;
        }
        setEstimate(data);
        setAiRunResult(null);
    }

    async function handleRunAiReview() {
        const result = await apiAdminRunIaQuizAiReview();
        setAiRunResult(result);
        reload();
    }

    return (
        <div>
            {dashboard && (
                <div style={{display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20}}>
                    <div style={{padding: 12, border: `1px solid ${C.border}`, borderRadius: 8}}>
                        Signalements ouverts : <strong>{dashboard.openReportsCount}</strong>
                    </div>
                    <div style={{padding: 12, border: `1px solid ${C.border}`, borderRadius: 8}}>
                        Questions en retard de revue : <strong>{dashboard.overdueCount}</strong>
                    </div>
                    <div style={{padding: 12, border: `1px solid ${C.border}`, borderRadius: 8}}>
                        Tentatives enregistrées : <strong>{dashboard.attemptsCount}</strong>
                    </div>
                </div>
            )}

            <div style={{marginBottom: 20, padding: 16, border: `1px solid ${C.border}`, borderRadius: 10}}>
                <h3 style={{marginTop: 0}}>Cycle de revue</h3>
                <p style={{color: C.textMuted, fontSize: 13}}>
                    Bascule en <code>review_required</code> les questions actives dont la date de revue est
                    dépassée — détection mécanique, aucun appel au modèle.
                </p>
                <button style={styles.button} onClick={runReviewCycle}>Lancer le cycle de revue (dates)</button>
                {reviewCycleResult && (
                    <p>{reviewCycleResult.affected.length} question(s) basculée(s) en revue requise.</p>
                )}
            </div>

            <div style={{marginBottom: 20, padding: 16, border: `1px solid ${C.border}`, borderRadius: 10}}>
                <h3 style={{marginTop: 0}}>Veille IA</h3>
                <p style={{color: C.textMuted, fontSize: 13}}>
                    Réévalue par IA les questions en <code>review_required</code> : chaque question produit soit
                    « pas de changement nécessaire », soit un brouillon de correction — jamais publié automatiquement.
                </p>
                <button style={styles.button} onClick={handleEstimate}>Estimer le coût</button>
                {estimate && (
                    <p>
                        {estimate.candidateCount} question(s) à réviser — coût estimé : ${estimate.estimatedCostUsd}
                        {" "}({estimate.model})
                    </p>
                )}
                <button style={styles.button} onClick={handleRunAiReview} disabled={!estimate}>
                    Lancer la veille IA
                </button>
                {aiRunResult && (
                    <p>
                        {aiRunResult.draftsCreated.length} brouillon créé(s), {aiRunResult.noChangeNeeded.length} sans
                        changement, {aiRunResult.errors.length} erreur(s).
                    </p>
                )}
            </div>

            <div style={{marginBottom: 20, padding: 16, border: `1px solid ${C.border}`, borderRadius: 10}}>
                <h3 style={{marginTop: 0}}>Signalements ouverts</h3>
                {reports.length === 0 && <p style={{color: C.textMuted}}>Aucun signalement ouvert.</p>}
                {reports.map(r => (
                    <div key={r.id} style={{display: "flex", justifyContent: "space-between", marginBottom: 6}}>
                        <span>Question #{r.question_id} — {r.reason}</span>
                        <button onClick={() => resolveReport(r.id)}>Classer</button>
                    </div>
                ))}
            </div>

            <div style={{marginBottom: 20, display: "flex", gap: 8, flexWrap: "wrap"}}>
                <select value={filters.axis} onChange={e => setFilters({...filters, axis: e.target.value})}>
                    <option value="">Tous les axes</option>
                    {AXES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
                    <option value="">Tous les statuts</option>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input placeholder="Recherche" value={filters.search}
                       onChange={e => setFilters({...filters, search: e.target.value})}/>
            </div>

            <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 24}}>
                <thead>
                    <tr>
                        <th style={{textAlign: "left"}}>Question</th>
                        <th>Axe</th><th>Niveau</th><th>Statut</th><th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {questions.map(q => (
                        <React.Fragment key={q.id}>
                            <tr style={{borderTop: `1px solid ${C.border}`}}>
                                <td>{q.prompt}</td>
                                <td>{q.axis}</td>
                                <td>{q.level}</td>
                                <td>{q.status}</td>
                                <td style={{display: "flex", gap: 6, flexWrap: "wrap"}}>
                                    <button onClick={() => startEdit(q)}>Modifier</button>
                                    <button onClick={() => startDuplicate(q)}>Dupliquer</button>
                                    <button onClick={() => transition("activate", q)}>Activer</button>
                                    <button onClick={() => transition("suspend", q)}>Suspendre</button>
                                    <button onClick={() => transition("archive", q)}>Archiver</button>
                                    <button onClick={() => flagReview(q)}>Signaler à revoir</button>
                                    <button onClick={() => toggleHistory(q)}>
                                        {historyFor === q.id ? "Masquer l'historique" : "Historique"}
                                    </button>
                                </td>
                            </tr>
                            {historyFor === q.id && (
                                <tr>
                                    <td colSpan={5} style={{background: "#F4FAF6", fontSize: 13, padding: 10}}>
                                        {history.length === 0
                                            ? "Aucune revue enregistrée pour cette question."
                                            : history.map(rev => (
                                                <div key={rev.id}>
                                                    {rev.old_status} → {rev.new_status}
                                                    {rev.reason ? ` — ${rev.reason}` : ""}
                                                    {" "}({new Date(rev.created_at).toLocaleDateString("fr-FR")})
                                                </div>
                                            ))}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <form onSubmit={submitForm} style={{padding: 16, border: `1px solid ${C.border}`, borderRadius: 10}}>
                <h3 style={{marginTop: 0}}>{form.id ? `Modifier la question #${form.id}` : "Nouvelle question (brouillon)"}</h3>
                <div style={{display: "flex", gap: 8, marginBottom: 8}}>
                    <select value={form.axis} onChange={e => updateForm("axis", e.target.value)}>
                        {AXES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={form.level} onChange={e => updateForm("level", Number(e.target.value))}>
                        {[1, 2, 3, 4, 5, 6].map(l => <option key={l} value={l}>Niveau {l}</option>)}
                    </select>
                </div>
                <textarea placeholder="Énoncé" value={form.prompt} onChange={e => updateForm("prompt", e.target.value)}
                          style={{width: "100%", marginBottom: 8}} required/>
                <input placeholder="Indice" value={form.hint} onChange={e => updateForm("hint", e.target.value)}
                       style={{width: "100%", marginBottom: 8}} required/>
                {["optionA", "optionB", "optionC", "optionD"].map((field, i) => (
                    <input key={field} placeholder={`Option ${String.fromCharCode(65 + i)}`} value={form[field]}
                           onChange={e => updateForm(field, e.target.value)} style={{width: "100%", marginBottom: 8}} required/>
                ))}
                <select value={form.correctOption} onChange={e => updateForm("correctOption", Number(e.target.value))}
                        style={{marginBottom: 8}}>
                    {[0, 1, 2, 3].map(i => <option key={i} value={i}>Bonne réponse : {String.fromCharCode(65 + i)}</option>)}
                </select>
                <textarea placeholder="Explication" value={form.feedback} onChange={e => updateForm("feedback", e.target.value)}
                          style={{width: "100%", marginBottom: 8}} required/>
                <textarea placeholder="Notes de source (facultatif)" value={form.sourceNotes}
                          onChange={e => updateForm("sourceNotes", e.target.value)} style={{width: "100%", marginBottom: 8}}/>
                <div style={{display: "flex", gap: 8}}>
                    <button type="submit" style={styles.button}>{form.id ? "Enregistrer" : "Créer le brouillon"}</button>
                    {form.id && <button type="button" onClick={() => setForm(EMPTY_FORM)}>Annuler</button>}
                </div>
            </form>

            {message && <p style={{color: C.primary}}>{message}</p>}
        </div>
    );
}
